import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import { BatchUpdateBottleOrders as BatchUpdateBottleOrdersService } from "../../services/BottleOrdersService";
import { bottleOrderStatusList } from "../common/statusLists";

const SHIP_BY_OPTIONS = [
  "UPS",
  "Pickup Madison Lab",
  "Pickup Carmel Lab",
  "Pickup Lafayette Lab",
  "FedEx",
  "Routine Route-Black",
  "Routine Route-Black/Gold",
  "Routine Route-Orange",
  "Routine Route-Blue",
  "Routine Route-Red",
  "Routine Route-Purple",
  "Routine Route-Green",
  "Routine Route",
  "ClientWalkIn",
  "PickUp",
  "UPS - with Return Label",
];

const FIELDS = [
  { label: "Kit Prep Due Date", name: "kitPrepDueDate", type: "date" },
  { label: "Shipping Due Date", name: "shippingDueDate", type: "date" },
  { label: "Location", name: "location", type: "text" },
  { label: "Comments", name: "comments", type: "text" },
  { label: "Shipping Instructions", name: "shippingInstructions", type: "text" },
  { label: "Tracking Comments", name: "trackingComments", type: "text" },
  { label: "Monitor Start Dates", name: "monitorStartDate", type: "date" },
  { label: "Monitor End Dates", name: "monitorEndDate", type: "date" },
];


const KIT_FIELDS = [
  { label: "Kit Built By", name: "kitBuiltBy", type: "text" },
  { label: "Kit Reviewed By", name: "kitReviewedBy", type: "text" },
  { label: "Kit Built On", name: "kitBuiltOn", type: "date" },
  { label: "Kit Reviewed On", name: "kitReviewedOn", type: "date" },
];

const emptyForm = () =>
  FIELDS.reduce(
    (acc, f) => ({ ...acc, [f.name]: "" }),
    {
      shippingStatus: "",
      shipBy: "",
      // "" = no change, "true" = firm, "false" = not firm
      shippingDueDateFirm: "",
      ...KIT_FIELDS.reduce((kAcc, f) => ({ ...kAcc, [f.name]: "" }), {}),
    }
  );

const BatchUpdateBottleOrders = ({ open, onClose, selectedOrders = [], onSuccess }) => {
  const [formData, setFormData] = useState(emptyForm());
  // Date fields the user explicitly wants blanked out on the selected orders
  const [clearFields, setClearFields] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(emptyForm());
      setClearFields({});
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearToggle = (name, checked) => {
    setClearFields((prev) => ({ ...prev, [name]: checked }));
    if (checked) setFormData((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const fieldsToUpdate = Object.entries(formData).reduce((acc, [key, val]) => {
      if (key === "shippingDueDateFirm") {
        if (val !== "") acc[key] = val === "true";
      } else if (val.trim() !== "") {
        acc[key] = val.trim();
      }
      return acc;
    }, {});

    const toClear = Object.keys(clearFields).filter((k) => clearFields[k]);
    if (toClear.length > 0) fieldsToUpdate.clearFields = toClear;

    if (Object.keys(fieldsToUpdate).length === 0) {
      NotificationManager.warning("Please enter at least one field to update.");
      return;
    }

    const payload = {
      orderNumbers: selectedOrders.map((o) => o.orderNumber),
      fields: fieldsToUpdate,
    };

    try {
      setLoading(true);
      await BatchUpdateBottleOrdersService(payload);
      NotificationManager.success(
        `Successfully updated ${selectedOrders.length} bottle order(s).`
      );
      setFormData(emptyForm());
      setClearFields({});
      onSuccess?.();
    } catch (error) {
      NotificationManager.error("Error performing batch update.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm());
    setClearFields({});
    onClose();
  };

  const dateFields = FIELDS.filter((f) => f.type === "date");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Batch Update Bottle Orders</DialogTitle>

      <DialogContent>
        <CommonLoader loading={loading} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Updating {selectedOrders.length} order(s):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
          {selectedOrders.map((o) => (
            <Chip
              key={o.orderNumber}
              label={`#${o.orderNumber}${o.shippingName ? ` — ${o.shippingName}` : ""}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave a field blank to skip updating it.
        </Typography>

        {/* Date Fields — 4 per row */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Dates
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {dateFields.map((field) => (
            <Grid item xs={12} sm={6} md={3} key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                variant="outlined"
                size="small"
                type="date"
                disabled={!!clearFields[field.name]}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!clearFields[field.name]}
                    onChange={(e) => handleClearToggle(field.name, e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Clear</Typography>}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="shipping-due-date-firm-label">Shipping Due Date Firm</InputLabel>
              <Select
                labelId="shipping-due-date-firm-label"
                label="Shipping Due Date Firm"
                name="shippingDueDateFirm"
                value={formData.shippingDueDateFirm}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>No change</em>
                </MenuItem>
                <MenuItem value="true">Firm</MenuItem>
                <MenuItem value="false">Not Firm</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Kit Built / Kit Reviewed — 4 per row */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Kit Prep &amp; Review
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Kit Built By"
              name="kitBuiltBy"
              value={formData.kitBuiltBy}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Kit Reviewed By"
              name="kitReviewedBy"
              value={formData.kitReviewedBy}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Kit Built On"
              name="kitBuiltOn"
              value={formData.kitBuiltOn}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Kit Reviewed On"
              name="kitReviewedOn"
              value={formData.kitReviewedOn}
              onChange={handleChange}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Details Fields — 4 per row for the short controls, full-size
            rows for the 3 comment/instruction textareas at the end */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="ship-by-label">Ship By</InputLabel>
              <Select
                labelId="ship-by-label"
                label="Ship By"
                name="shipBy"
                value={formData.shipBy}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {SHIP_BY_OPTIONS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="shipping-status-label">Shipping Status</InputLabel>
              <Select
                labelId="shipping-status-label"
                label="Shipping Status"
                name="shippingStatus"
                value={formData.shippingStatus}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {bottleOrderStatusList.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Comments, Shipping Instructions, Tracking Comments stay as
              larger multiline controls, unaffected by the 4-per-row change */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Comments"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Shipping Instructions"
              name="shippingInstructions"
              value={formData.shippingInstructions}
              onChange={handleChange}
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tracking Comments"
              name="trackingComments"
              value={formData.trackingComments}
              onChange={handleChange}
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update {selectedOrders.length} Order(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchUpdateBottleOrders;